import React, { createContext, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {instance} from "../../api/fetch";

const LoadingContext = createContext();

export const useLoading = () => useContext(LoadingContext);

/**
 * 전역 로딩 오버레이.
 *
 * ## 🔴 boolean → **진행 중 요청 수 세기**(2026-09-11)
 *
 * 종전에는 `loading` 이 boolean 이고 **응답 하나가 오면 무조건 `false`** 였다.
 * 화면이 요청을 여러 개 동시에 보내면(대부분의 화면이 그렇다) **빠른 응답 하나가
 * 느린 요청의 스피너를 꺼 버린다.**
 *
 * 실제 신고(2026-09-11): '고객 분석' 탭은 `getDetailProject`(빠름) ·
 * `getEntryPointsToProject`(빠름) · `getKakaoUsersStatistics`(느림, 5초+)를 함께 부른다.
 * 빠른 두 개가 먼저 끝나 오버레이가 사라지고 **나머지 5초 동안 화면이 멈춘 것처럼** 보였다.
 *
 * ⇒ 진행 중 요청 수를 세고 **0이 될 때만** 감춘다.
 *
 * 🔑 **음수로 내려가지 않게 막는다.** 요청 인터셉터가 돌기 전에 실패하는 경로
 * (`isChannelSwitched()` 차단 등)에서는 응답만 오거나 아무 것도 안 올 수 있다.
 * 음수가 되면 그 뒤의 정상 요청이 스피너를 못 켠다.
 */
export const LoadingProvider = ({ children }) => {
    const [pending, setPending] = useState(0);
    const loading = pending > 0;

    useEffect(() => {
        const begin = () => setPending((n) => n + 1);
        const end = () => setPending((n) => Math.max(0, n - 1));

        const requestInterceptor = instance.interceptors.request.use((config) => {
            if (!config.skipLoading) {
                begin();
            }
            return config;
        });

        const responseInterceptor = instance.interceptors.response.use(
            (response) => {
                if (!response.config.skipLoading) {
                    end();
                }
                return response;
            },
            (error) => {
                // 에러 발생 시에도 config를 확인합니다.
                if (!error.config?.skipLoading) {
                    end();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            instance.interceptors.request.eject(requestInterceptor);
            instance.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    return (
        <LoadingContext.Provider value={{ loading }}>
            {children}
            {loading && ReactDOM.createPortal(<LoadingOverlay />, document.body)}
        </LoadingContext.Provider>
    );
};

const LoadingOverlay = () => (
    <div className="loading-overlay">
        <div className="loading-spinner"></div>
    </div>
);